import type { FC, ReactElement, ReactNode } from 'react';
import { cloneElement, memo, useEffect, useMemo, useRef } from 'react';
import {
  WarningFilled,
  CheckCircleFilled,
  ExclamationCircleFilled,
  LoadingOutlined,
} from '@ant-design/icons';
import type { NodeProps } from '../../types';
import { BlockEnum, NodeRunningStatus } from '../../types';
import { useNodesReadOnly } from '../../hooks';
import { useBoolean, useHover } from 'ahooks';
// import { hasErrorHandleNode, hasRetryNode } from '../../utils';
// import { useNodeIterationInteractions } from '../iteration/use-interactions';
import { NodeSourceHandle, NodeTargetHandle } from './components/node-handle';
import NodeControl from './components/node-control';
// import ErrorHandleOnNode from './components/error-handle/error-handle-on-node';
// import RetryOnNode from './components/retry/retry-on-node';
// import AddVariablePopupWithPosition from './components/add-variable-popup-with-position';
import cn from '@/utils/classnames';
import BlockIcon from '../../icons/block-icon';
import { Tag } from 'antd';
import { NODE_DEFAULT_BACKGROUND_COLOR } from '../constants';
import RoleDesc from './components/role-desc';
import { useWorkflowStore } from '../../store';

type BaseNodeProps = {
  children: ReactNode;
} & NodeProps;

const BaseNode: FC<BaseNodeProps> = ({ id, data, children }) => {
  const stationRef = useRef(null);
  const isHovering = useHover(stationRef);
  const nodeRef = useRef<HTMLDivElement>(null);
  const workflowStore = useWorkflowStore();
  const { isPreview } = workflowStore.getState()
  const { nodesReadOnly } = useNodesReadOnly();
  const { bgColor = NODE_DEFAULT_BACKGROUND_COLOR } = data;
  // const { handleNodeIterationChildSizeChange } = useNodeIterationInteractions();
  // const toolIcon = useToolIcon(data);

  /*   useEffect(() => {
    if (nodeRef.current && data.selected && data.isInIteration) {
      const resizeObserver = new ResizeObserver(() => {
        handleNodeIterationChildSizeChange(id);
      });

      resizeObserver.observe(nodeRef.current);

      return () => {
        resizeObserver.disconnect();
      };
    }
  }, [
    data.isInIteration,
    data.selected,
    id,
    handleNodeIterationChildSizeChange,
  ]); */
  const showSelectedBorder =
    data.selected || data._isBundled || data._isEntering;
  const {
    showRunningBorder,
    showSuccessBorder,
    showFailedBorder,
    showExceptionBorder,
  } = useMemo(() => {
    return {
      showRunningBorder:
        data._runningStatus === NodeRunningStatus.Running &&
        !showSelectedBorder,
      showSuccessBorder:
        data._runningStatus === NodeRunningStatus.Succeeded &&
        !showSelectedBorder,
      showFailedBorder:
        data._runningStatus === NodeRunningStatus.Failed && !showSelectedBorder,
      showExceptionBorder:
        data._runningStatus === NodeRunningStatus.Exception &&
        !showSelectedBorder,
    };
  }, [data._runningStatus, showSelectedBorder]);

  return (
    <div
      className={cn(
        "flex rounded-2xl border-[2px]",
        showSelectedBorder && data.type !== BlockEnum.Mirror
          ? "border-components-option-card-option-selected-border"
          : "border-transparent",
        !showSelectedBorder &&
          data._inParallelHovering && showSelectedBorder && data.type !== BlockEnum.Mirror &&
          "border-workflow-block-border-highlight",
        data._waitingRun && "opacity-70",
        data.type === BlockEnum.NestedFlow && "bg-green-100",
        data.type === BlockEnum.NestedFlow && isPreview && "hover:bg-green-200",
      )}
      ref={nodeRef}
      onDoubleClick={() => {
        if(data.type != BlockEnum.NestedFlow) return;
        if(!isPreview) return;
        if(!data.detail?.nestedFlowInfo?.id) return;
        window.open('/BfDetail?bfId=' + data.detail?.nestedFlowInfo?.id)
      }}
      style={{
        width: "auto",
        height: "auto",
      }}
    >
      <div
        className={cn(
          "group relative pb-1",
          data.type !== BlockEnum.Mirror && 'shadow-xs',
          "rounded-[15px] border border-transparent",
          data.type !== BlockEnum.Start &&
            data.type !== BlockEnum.End &&
            data.type !== BlockEnum.Mirror
            ? "w-[240px]"
            : "w-[50px] text-center",
          (data.type === BlockEnum.AssignmentOffline ||
            data.type === BlockEnum.AssignmentOnline)
            ? `${bgColor}`
            : (data.type !== BlockEnum.Mirror && data.type !== BlockEnum.NestedFlow && "bg-workflow-block-bg"),
          !data._runningStatus && data.type !== BlockEnum.Mirror && "hover:shadow-lg",
          showRunningBorder && "!border-state-accent-solid",
          showSuccessBorder && "!border-state-success-solid",
          showFailedBorder && "!border-state-destructive-solid",
          showExceptionBorder && "!border-state-warning-solid",
          data._isBundled && "!shadow-lg"
        )}
      >
        {data._inParallelHovering && (
          <div className="top system-2xs-medium-uppercase absolute -top-2.5 left-2 z-10 text-text-tertiary">
            {"并行运行"}
          </div>
        )}
        {/* {data._showAddVariablePopup && (
          <AddVariablePopupWithPosition nodeId={id} nodeData={data} />
        )} */}
        {!data._isCandidate && (
          <NodeTargetHandle
            id={id}
            data={data}
            handleClassName="!top-4 !-left-[9px] !translate-y-0"
            handleId="target"
          />
        )}
        {!data._isCandidate && (
          <NodeSourceHandle
            id={id}
            data={data}
            handleClassName="!top-4 !-right-[9px] !translate-y-0"
            handleId="source"
          />
        )}
        {!data._runningStatus && !nodesReadOnly && !data._isCandidate && (
          <NodeControl id={id} data={data} />
        )}
        {data.type !== BlockEnum.Start && data.type !== BlockEnum.End && data.type !== BlockEnum.Mirror && data.type !== BlockEnum.NestedFlow && (
          <div className={cn("flex items-center rounded-t-2xl px-3 pb-2 pt-3")}>
            <BlockIcon className="mr-2 shrink-0" type={data.type} size="md" />
            <div
              title={data.title}
              className="system-sm-semibold-uppercase mr-1 flex grow items-center truncate text-text-primary"
            >
              <div
                className={cn(
                  (data.type === BlockEnum.AssignmentOffline ||
                    data.type === BlockEnum.AssignmentOnline) &&
                    "text-center w-full truncate"
                )}
              >
                {data.title}
              </div>
            </div>
            {data._iterationLength &&
              data._iterationIndex &&
              data._runningStatus === NodeRunningStatus.Running && (
                <div className="mr-1.5 text-xs font-medium text-text-accent">
                  {data._iterationIndex > data._iterationLength
                    ? data._iterationLength
                    : data._iterationIndex}
                  /{data._iterationLength}
                </div>
              )}
            {data._loopLength &&
              data._loopIndex &&
              data._runningStatus === NodeRunningStatus.Running && (
                <div className="mr-1.5 text-xs font-medium text-primary-600">
                  {data._loopIndex > data._loopLength
                    ? data._loopLength
                    : data._loopIndex}
                  /{data._loopLength}
                </div>
              )}
            {(data._runningStatus === NodeRunningStatus.Running ||
              data._singleRunningStatus === NodeRunningStatus.Running) && (
              <LoadingOutlined className="h-3.5 w-3.5 animate-spin text-text-accent" />
            )}
            {data._runningStatus === NodeRunningStatus.Succeeded && (
              <CheckCircleFilled className="h-3.5 w-3.5 text-text-success" />
            )}
            {data._runningStatus === NodeRunningStatus.Failed && (
              <ExclamationCircleFilled className="h-3.5 w-3.5 text-text-destructive" />
            )}
            {data._runningStatus === NodeRunningStatus.Exception && (
              <WarningFilled className="h-3.5 w-3.5 text-text-warning-secondary" />
            )}
          </div>
        ) }
        {(data.type == BlockEnum.Start || data.type == BlockEnum.End) && (
          <div className={cn("flex items-center rounded-t-2xl px-3 pb-2 pt-3")}>
            <BlockIcon className="mr-2 shrink-0" type={data.type} size="md" />
          </div>
        )}

        {(data.type === BlockEnum.NestedFlow) && (
          <div className={cn("flex items-center rounded-t-2xl px-3 pb-2 pt-3")}>
            <BlockIcon className="mr-2 shrink-0" type={data.type} size="md" />
            {data.detail?.nestedFlowInfo?.targetType && <Tag color='lime'>{data.detail?.nestedFlowInfo?.targetType}</Tag>}
            <div
              title={data.detail?.nestedFlowInfo?.name}
              className={cn("system-sm-semibold-uppercase mr-1 grow items-center",
                data.detail?.nestedFlowInfo?.name ? "text-text-success" : "text-text-warning"
              )}
            >
              {data.detail?.nestedFlowInfo?.name ? data.detail?.nestedFlowInfo?.name : `(未选择关联流程)`}
            </div>
          </div>
        )}

        {(data.type === BlockEnum.AssignmentOffline ||
          data.type === BlockEnum.AssignmentOnline) && (typeof data.detail?.station == 'string' ? data.detail?.station : data.detail.station?.name) && (
          <>
          <div
            ref={stationRef}
            className="system-sm-semibold-uppercase mr-1 flex grow items-center truncate text-text-primary pl-3 mb-2 relative"
          >
            {data.detail?.station && (
              <Tag className="text-xs max-w-full truncate" color="blue">
                {(typeof data.detail?.station == 'string' ? data.detail?.station : data.detail.station?.name)}
              </Tag>
            )}
          </div>
          {
            isHovering && typeof data.detail?.station != 'string' && data.detail?.station?.id && <RoleDesc id={data.detail?.station.id} />
          }
        </>
        )}
        {cloneElement(children as ReactElement, { id, data })}
        {/* {hasRetryNode(data.type) && <RetryOnNode id={id} data={data} />} */}
        {/* {hasErrorHandleNode(data.type) && (
          <ErrorHandleOnNode id={id} data={data} />
        )} */}
        {/* {data.desc &&
          (data.type === BlockEnum.Start || data.type === BlockEnum.End) && (
            <div className="system-xs-regular whitespace-pre-line break-words px-3 pb-2 pt-1 text-text-tertiary">
              {data.desc}
            </div>
          )} */}
      </div>
    </div>
  );
};

export default memo(BaseNode);
